import React from 'react';
import {
  Badge,
  Divider,
  Drawer,
  NavLink,
  Stack,
} from '@mantine/core';
import {
  IconBell,
  IconBrandWhatsapp,
  IconCalendar,
  IconCurrencyEuro,
  IconDialpad,
  IconLogout,
  IconMessage,
  IconSearch,
  IconUser,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { RouteUtils } from '@bobex/react-core/routes/RouteUtils';
import { AccountService } from '@bobex/react-core/services/AccountService';

export interface ToolsDrawerProps {
  opened: boolean;
  onClose: () => void;
  onOpenSearch: () => void;
  onOpenIncall: () => void;
  onOpenWhatsApp?: () => void;
  whatsappUnreadCount?: number;
  reminderCount?: number;
  user: { userID?: number; firstname?: string; lastname?: string } | null;
}

export default function ToolsDrawer({
  opened,
  onClose,
  onOpenSearch,
  onOpenIncall,
  onOpenWhatsApp,
  whatsappUnreadCount = 0,
  reminderCount = 0,
  user,
}: ToolsDrawerProps) {
  const { t } = useTranslation();
  const onCallCalendar = useLocation().pathname.includes('call-calendar');
  const isNl = RouteUtils.getContext() === 'nl';

  const handleSearch = () => {
    onOpenSearch();
    onClose();
  };

  const handleIncall = () => {
    onOpenIncall();
    onClose();
  };

  const handleWhatsApp = () => {
    onOpenWhatsApp?.();
    onClose();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('Tools')}
      position="right"
      size="sm"
      padding="md"
      withCloseButton
    >
      <Stack gap={0}>
        <NavLink
          label={t('Search')}
          description="ctrl + K"
          leftSection={<IconSearch size={20} stroke={1.5} />}
          onClick={handleSearch}
        />
        {isNl && onOpenWhatsApp != null && (
          <NavLink
            label="WhatsApp"
            leftSection={<IconBrandWhatsapp size={20} stroke={1.5} />}
            rightSection={
              whatsappUnreadCount > 0 ? (
                <Badge size="sm" color="red" variant="filled">
                  {whatsappUnreadCount}
                </Badge>
              ) : undefined
            }
            onClick={handleWhatsApp}
          />
        )}

        <Divider my="xs" />

        <NavLink
          label={t('Unpaid clients')}
          leftSection={<IconCurrencyEuro size={20} stroke={1.5} />}
        />
        <NavLink
          label={t('Reminders')}
          leftSection={<IconBell size={20} stroke={1.5} />}
          rightSection={
            reminderCount > 0 ? (
              <Badge size="sm" color="red" variant="filled">
                {reminderCount}
              </Badge>
            ) : undefined
          }
          component="a"
          href={
            user?.userID
                    ? RouteUtils.getBasename() + '/call-calendar'
                    : '#'
          }
          target={onCallCalendar ? '_self' : '_blank'}
          rel="noopener noreferrer"
          onClick={onClose}
        />
        <NavLink
          label={t('Calendar')}
          leftSection={<IconCalendar size={20} stroke={1.5} />}
          component="a"
          href={
            user?.userID
                    ? RouteUtils.getBasename() + '/call-calendar'
                    : '#'
          }
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
        />
        <NavLink
          label={t('Messages')}
          leftSection={<IconMessage size={20} stroke={1.5} />}
        />

        <Divider my="xs" />

        <NavLink
          label={t('Incall')}
          leftSection={<IconDialpad size={20} stroke={1.5} />}
          onClick={handleIncall}
        />

        <Divider my="xs" />

        <NavLink
          label={user ? `${user.firstname} ${user.lastname}` : t('Account')}
          leftSection={<IconUser size={20} stroke={1.5} />}
          active
          variant="light"
          disableRightSectionRotation
        />
        <NavLink
          label={t('Logout')}
          leftSection={<IconLogout size={20} stroke={1.5} />}
          onClick={() => AccountService.logout()}
          color="red"
        />
      </Stack>
    </Drawer>
  );
}
